import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Table, Tag, Tooltip, notification } from "antd";
import { useLocation } from "react-router-dom";
import { FAST_API_URL } from "../../../../../api";
import { startSiteLogin } from "../../../../../api/streamEditorAuth";
import { useAuth, useStreamEditorFeature } from "../../../../../providers/AuthProvider";
import {
    acceptedLine,
    ASK_FIX_STATE,
    closedLine,
    fixEvents,
    fixLine,
    hhmm,
    isDriftQuestion,
    isMine,
    isQuestionOpen,
    langOf,
    OPEN_FIX_STATES,
    openAiFixes,
    openFixesLine,
    passWaitOf,
    passWaitTag,
    PROPOSED_AGAIN_HINT,
    proposedPending,
    proposedTexts,
    questionState,
    STATE_COLOR,
    STATE_MARK,
    STEP_TITLES,
    StepState,
    StreamFix,
    StreamProgressData,
    viewersLine,
    visibleFixes,
} from "./model";
import { releaseFolder } from "./release";
import { FixQuestion } from "./FixQuestion";
import { StreamDone } from "../StreamDone";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { folderToRelease } from "../../streamLang";

const POLL_MS = 5000;
const LAST_FOLDER_KEY = "stream_editor_last_folder";

const rememberedFolder = (mediaId: string): { folder: number; lang: string } | null => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(LAST_FOLDER_KEY) || "null");
        return saved && saved.media === mediaId ? { folder: Number(saved.folder), lang: String(saved.lang) } : null;
    } catch {
        return null;
    }
};

const rememberFolder = (mediaId: string, folder: number, lang: string) => {
    try {
        sessionStorage.setItem(LAST_FOLDER_KEY, JSON.stringify({ media: mediaId, folder, lang }));
    } catch {
        /* storage off — release works within this page only */
    }
};
const STEPS = ["ru", "translate", "voice", "air"];

/** Ключ тоста вопроса правки: закрылся вопрос (по правилу TAG) — этот тост убирается. */
const askKey = (fixId: string) => `stream-fix-ask-${fixId}`;
/** Ключ тоста закрытого вопроса: один на вопрос — повтор после перезагрузки не множит тосты. */
const closedKey = (fixId: string) => `stream-fix-closed-${fixId}`;

/**
 * Какие тосты закрытых вопросов с текстом, который не влез, человек закрыл сам — по эфиру, в этом браузере: после
 * перезагрузки такой тост снова не всплывает. Хранилище выключено — помнится только на этой странице.
 */
const DISMISSED_KEY = "stream_editor_dismissed_questions";
const readDismissed = (mediaId: string): Set<string> => {
    try {
        const saved = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "{}");
        const ids = saved && Array.isArray(saved[mediaId]) ? saved[mediaId] : [];
        return new Set<string>(ids.map((id: unknown) => String(id)));
    } catch {
        return new Set<string>();
    }
};
const saveDismissed = (mediaId: string, ids: Set<string>) => {
    try {
        const saved = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "{}") || {};
        saved[mediaId] = Array.from(ids).slice(-200);
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(saved));
    } catch {
        /* storage off — помнится только на этой странице */
    }
};

/**
 * Тег правки в окне «папки и правки»: у вопроса — его ход, как его считает TAG (ждёт ответа / ответ принят, а новая
 * правка ещё идёт / закрыт), а не машинное `needs_translator`.
 */
const fixTag = (fx: StreamFix, fixes: StreamFix[]): { color: string; text: string } => {
    const phase = questionState(fx, fixes).phase;
    if (phase === "closed") return { color: "default", text: "вопрос закрыт" };
    if (phase === "answered") return { color: "processing", text: "ответ принят" };
    if (fx.state === ASK_FIX_STATE) return { color: "warning", text: "вопрос вам" };
    if (fx.state === "fixed") return { color: "green", text: fx.state };
    // Ждёт конца прохода своей папки (P50m10e) — так и сказано, а не машинное `queued`.
    const wait = OPEN_FIX_STATES.includes(fx.state) ? passWaitOf(fx) : null;
    if (wait) return { color: "processing", text: passWaitTag(wait) };
    if (OPEN_FIX_STATES.includes(fx.state)) return { color: "processing", text: fx.state };
    if (fx.state === "nothing_to_fix") return { color: "default", text: fx.state };
    return { color: "error", text: fx.state };
};

/**
 * Под срезом «папки и правки»: сколько ранних правок не показано и что среди них нет открытых вопросов; закрытые вами
 * тосты вопросов с текстом, который не влез, — названы числом (текст переводчика не пропадает молча).
 */
export const hiddenLine = (fixes: StreamFix[], dismissed: ReadonlySet<string>): string => {
    const shown = new Set(visibleFixes(fixes, dismissed).map((fx) => fx.id));
    const hidden = fixes.filter((fx) => !shown.has(fx.id));
    const closedByYou = hidden.filter((fx) => dismissed.has(fx.id) && proposedPending(fx, fixes)).length;
    return (
        `и ещё ${hidden.length} более ранних правок — открытых вопросов среди них нет` +
        (closedByYou ? `; вопросов с текстом, который не влез, чей тост вы закрыли: ${closedByYou}` : "")
    );
};

/** Подсказка блока статусов: на узком окне его хвост обрезан — целиком он здесь. */
export const statusTitle = (
    viewers: string | null | undefined,
    openFixes: number,
    error: string | null,
    openLine?: string,
): string =>
    [
        "Подробности: все папки эфира и правки",
        openFixes > 0 ? openLine || `правок в работе: ${openFixes}` : "",
        error ? `прогресс не получен: ${error}` : "",
        viewers || "",
    ]
        .filter(Boolean)
        .join(" · ");

interface Props {
    mediaId: string;
    folder: number;
    lang: string;
    isRealtimeStream: boolean;
}

/**
 * Полоса прогресса папки эфира и тост-отчёт правки (W4.4, фаза 4) — только переводчику эфира из списка
 * (вход через сайт, `features: stream_progress`). Остальным — ссылка «Войти через сайт» на эфире, и всё.
 *
 * Кратко: «русский ✓ — перевод ⟳ — голос · — в эфире · — зрители увидят в ЧЧ:ММ»; по клику — все папки и правки.
 * Правка переводчика уходит в эфир сама (полоса правок site_server): тост говорит «принята», потом итог —
 * «в эфире ЧЧ:ММ, зрители увидят в ЧЧ:ММ» или почему не вышло. Уход с папки — её правки в эфир сразу (release).
 */
export const StreamProgress: React.FC<Props> = ({ mediaId, folder, lang, isRealtimeStream }) => {
    const { user } = useAuth();
    const location = useLocation();
    const enabled = useStreamEditorFeature("stream_progress");
    const [data, setData] = useState<StreamProgressData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const seen = useRef<Map<string, string>>(new Map());
    const firstPass = useRef(true);
    const lastFolder = useRef<{ folder: number; lang: string } | null>(null);
    const [api, holder] = notification.useNotification();
    const originalLang = (useAudioStateContext() as { originalLang?: string } | undefined)?.originalLang;
    // Тосты закрытых вопросов с текстом, который не влез: какие человек закрыл (помнится и после перезагрузки) и какие
    // сейчас на экране (текст ушёл новой правкой — тост убирается сам).
    const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed(mediaId));
    const dismissedRef = useRef<Set<string>>(dismissed);
    dismissedRef.current = dismissed;
    const pendingShown = useRef<Set<string>>(new Set());
    const dismiss = useCallback(
        (fixId: string) => {
            const next = new Set(dismissedRef.current);
            next.add(fixId);
            dismissedRef.current = next;
            saveDismissed(mediaId, next);
            setDismissed(next);
        },
        [mediaId],
    );

    // Тост вопроса живёт дольше вызова, который его показал: ответ кнопкой спрашивает полосу последним `refresh`.
    const refreshRef = useRef<() => Promise<void>>(async () => undefined);

    const refresh = useCallback(async () => {
        if (!enabled || !mediaId) return;
        try {
            const response = await fetch(`${FAST_API_URL}/progress?media=${encodeURIComponent(mediaId)}`);
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                setError(body?.detail?.message || body?.detail || `прогресс не получен (${response.status})`);
                return;
            }
            setError(null);
            setData(body as StreamProgressData);
            const fixes: StreamFix[] = [];
            for (const f of (body as StreamProgressData).folders || []) {
                for (const l of Object.values(f.langs || {})) fixes.push(...(l.fixes || []));
            }
            const events = fixEvents(fixes, seen.current, user?.email || null, firstPass.current, dismissedRef.current);
            firstPass.current = false;
            // Текст, который не влез, ушёл новой правкой (или вопрос снова открыт) — тост закрытого вопроса не нужен.
            for (const id of Array.from(pendingShown.current)) {
                const fix = fixes.find((one) => one.id === id);
                if (!fix || !proposedPending(fix, fixes)) {
                    api.destroy(closedKey(id));
                    pendingShown.current.delete(id);
                }
            }
            for (const ev of events) {
                const where = `папка ${ev.fix.folder}`;
                if (ev.kind === "closed") {
                    // Вопрос закрыт по правилу TAG («оставить как в эфире», новая правка ответа закончилась, сервер
                    // закрыл сам): его тост с кнопками больше не нужен. Текст, который не влез, — в этом тосте и в окне
                    // «Эфир: папки и правки», с подсказкой, что его можно вписать снова. Тост висит, пока его не
                    // закроют, если этот текст не ушёл и новой правкой («оставить как в эфире», закрыл сервер): после
                    // «разрешить сократить соседнюю» он ушёл новой правкой, после «сокращу сам» — его заменил свой.
                    // Висит и после перезагрузки (`proposedPending`), пока человек не закрыл тост сам.
                    const proposed = proposedTexts(ev.fix);
                    const pending = proposedPending(ev.fix, fixes);
                    const fixId = ev.fix.id;
                    api.destroy(askKey(fixId));
                    if (pending) pendingShown.current.add(fixId);
                    api.info({
                        key: closedKey(fixId),
                        message: `Вопрос правки закрыт (${where})`,
                        description: proposed.length
                            ? `${closedLine(ev.fix, fixes)}. Ваш текст, который не влез: ${proposed
                                  .map((text) => `«${text}»`)
                                  .join(", ")} — ${PROPOSED_AGAIN_HINT}`
                            : closedLine(ev.fix, fixes),
                        placement: "bottomRight",
                        duration: pending ? 0 : proposed.length ? 15 : 8,
                        onClose: pending
                            ? () => {
                                  // Закрыл сам человек (или текст ушёл новой правкой — тогда помнить не вредно).
                                  pendingShown.current.delete(fixId);
                                  dismiss(fixId);
                              }
                            : undefined,
                    });
                } else if (ev.kind === "accepted") {
                    api.info({
                        message: `Правка принята (${where})`,
                        description: acceptedLine(ev.fix),
                        placement: "bottomRight",
                        duration: 4,
                    });
                } else if (ev.fix.state === "fixed") {
                    api.success({ message: `Правка в эфире (${where})`, description: fixLine(ev.fix),
                        placement: "bottomRight", duration: 15 });
                } else if (ev.fix.state === "nothing_to_fix") {
                    api.info({ message: `Правка (${where})`, description: fixLine(ev.fix), placement: "bottomRight" });
                } else if (ev.fix.state === ASK_FIX_STATE) {
                    // П.50г: правка не влезла и временем — не сбой, а вопрос переводчику; соседние реплики не тронуты.
                    // Правка времени дальше предела — так же. Кнопки ответа — ровно `question.answers` сервера. Ответ
                    // принят, а вопрос открыт (TAG: новая правка ещё идёт или короткой правки ещё нет) — тот же тост,
                    // обновлённый: текст, который не влез, на экране, пока вопрос не закрылся.
                    const waiting = questionState(ev.fix, fixes).phase === "answered";
                    api.warning({
                        key: askKey(ev.fix.id),
                        message: waiting
                            ? `Вопрос правки (${where}) — ответ принят, вопрос открыт`
                            : isDriftQuestion(ev.fix)
                              ? `Правка времени не выложена (${where}) — нужен ваш ответ`
                              : `Правка не влезает (${where}) — нужен ваш ответ`,
                        description: ev.fix.question ? (
                            <FixQuestion
                                mediaId={mediaId}
                                fix={ev.fix}
                                fixes={fixes}
                                onAnswered={() => void refreshRef.current()}
                            />
                        ) : (
                            `${fixLine(ev.fix)}. Сократите свой текст здесь или в плеере («Мои правки») ` +
                            "разрешите сократить соседнюю реплику."
                        ),
                        placement: "bottomRight",
                        duration: 0,
                    });
                } else {
                    api.error({ message: `Правка не дошла до эфира (${where})`, description: fixLine(ev.fix),
                        placement: "bottomRight", duration: 0 });
                }
            }
        } catch (err) {
            setError((err as Error).message);
        }
    }, [enabled, mediaId, api, user?.email, dismiss]);
    refreshRef.current = refresh;

    useEffect(() => {
        if (!enabled || !isRealtimeStream) return undefined;
        void refresh();
        const timer = window.setInterval(refresh, POLL_MS);
        return () => window.clearInterval(timer);
    }, [enabled, isRealtimeStream, refresh]);

    // Переводчик ушёл с папки — её ждущие правки уходят в эфир сразу (CONTRACTS п.15). Смена папки в редакторе
    // перезагружает страницу, поэтому прежняя папка помнится и в sessionStorage этой вкладки.
    useEffect(() => {
        if (!enabled || !isRealtimeStream || !folder) return;
        const prev = lastFolder.current || rememberedFolder(mediaId);
        lastFolder.current = { folder, lang };
        rememberFolder(mediaId, folder, lang);
        // Только папку языка перевода: русский столбец заявок правок не держит, `release` с `lang: "ru"` — 422
        // (проба P50m10c: заход на ?folder=1&lang=en после ?folder=1 без языка).
        const gone = folderToRelease(prev, { folder, lang }, originalLang);
        if (gone) {
            void releaseFolder(mediaId, gone.folder, gone.lang).catch(() => undefined);
        }
    }, [enabled, isRealtimeStream, folder, lang, mediaId, originalLang]);

    const current = useMemo(() => {
        const f = data?.folders?.find((item) => item.folder === Number(folder));
        return { folder: f, lang: langOf(f, lang) };
    }, [data, folder, lang]);

    if (!isRealtimeStream) return null;
    // «Готово — в эфир» и «← К эфиру» — рядом с полосой; кнопка сама знает, видна ли она (stream_fixes).
    const done = (
        <StreamDone
            mediaId={mediaId}
            folder={folder}
            lang={lang}
            isRealtimeStream={isRealtimeStream}
            data={enabled ? data : null}
            onReleased={() => void refresh()}
        />
    );
    if (!enabled) {
        if (user?.site) return done;
        return (
            <>
                {done}
                <Tooltip title="Переводчику эфира из списка: правки сами уходят в эфир, видно, как идут папки">
                    <a
                        style={{ fontSize: 11, marginRight: 8 }}
                        onClick={() => startSiteLogin(`${window.location.origin}${location.pathname}${location.search}`)}
                    >
                        Войти через сайт
                    </a>
                </Tooltip>
            </>
        );
    }

    const me = user?.email || null;
    const steps = current.lang?.steps;
    const viewers = viewersLine(current.folder?.public_at);
    const openFixes = current.lang?.fixes_open || 0;
    // Правки, что ждут конца прохода своей папки (P50m10e): шапка говорит это, а не просто «в работе». Замены ИИ
    // названы (P50m20d: две замены ИИ рядом с ♪ читались как «♪ считается за 2»).
    const openLine = openFixesLine(
        openFixes,
        current.lang?.fixes_wait_pass || 0,
        Number(folder),
        openAiFixes(current.lang),
    );

    const stepTag = (id: string, state?: StepState) => (
        <Tag key={id} color={STATE_COLOR[state || "pending"]} style={{ marginInlineEnd: 2, fontSize: 11 }}>
            {STEP_TITLES[id]} {STATE_MARK[state || "pending"]}
        </Tag>
    );

    const folderRows = (data?.folders || []).map((f) => {
        const l = langOf(f, lang);
        return {
            key: f.folder,
            folder: f.folder,
            steps: l?.steps,
            published: hhmm(l?.published_at),
            viewers: viewersLine(f.public_at),
            title: l?.steps?.title || l?.stage,
            fixes: l?.fixes || [],
        };
    });

    return (
        <>
            {holder}
            {/* Блок статусов шапки (`x_head_status`): одна строка, в шапке сжимается и обрезается внутри себя, —
                появление счётчика или статуса не переносит шапку на вторую строку и не сдвигает ленту (P50m10, E6).
                «Правок в работе» — первым: при 1280 px обрезка не прячет ход своей правки; «зрители видят с…» —
                последним: на узком окне обрезается первым; всё целиком — в подсказке. */}
            <span
                data-testid="stream-progress"
                className="x_head_status"
                style={{ fontSize: 11, marginRight: 8, cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={() => setOpen(true)}
                title={statusTitle(viewers, openFixes, error, openLine)}
            >
                {openFixes > 0 && (
                    <Tag
                        data-testid="stream-progress-open-fixes"
                        color="processing"
                        style={{ marginInlineEnd: 4, fontSize: 11 }}
                    >
                        {openLine}
                    </Tag>
                )}
                {steps ? STEPS.map((id) => stepTag(id, steps[id] as StepState)) : <Tag>прогресс…</Tag>}
                {error && (
                    <Tooltip title={error}>
                        <Tag color="error" style={{ marginLeft: 4, fontSize: 11 }}>
                            прогресс?
                        </Tag>
                    </Tooltip>
                )}
                {viewers && <span style={{ marginLeft: 4 }}>{viewers}</span>}
            </span>
            {done}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={980}
                title={`Эфир: папки и правки${data?.viewer_delay_sec ? ` · задержка зрителей ${Math.round(data.viewer_delay_sec / 60)} мин` : ""}`}
            >
                <Table
                    size="small"
                    pagination={false}
                    dataSource={folderRows}
                    rowClassName={(row) => (row.folder === Number(folder) ? "ant-table-row-selected" : "")}
                    columns={[
                        { title: "Папка", dataIndex: "folder", width: 64 },
                        {
                            title: "Шаги",
                            dataIndex: "steps",
                            render: (s) => (s ? STEPS.map((id) => stepTag(id, s[id])) : "—"),
                        },
                        { title: "Сейчас", dataIndex: "title", width: 180 },
                        { title: "В эфире", dataIndex: "published", width: 80 },
                        { title: "Зрители", dataIndex: "viewers", width: 160 },
                        {
                            title: "Правки",
                            dataIndex: "fixes",
                            render: (fixes: StreamFix[]) =>
                                fixes.length ? (
                                    <div>
                                        {visibleFixes(fixes, dismissed).map((fx) => (
                                            <div key={fx.id} style={{ fontSize: 11 }} data-fix-id={fx.id}>
                                                <Tag color={fixTag(fx, fixes).color}>{fixTag(fx, fixes).text}</Tag>
                                                {fixLine(fx)}
                                                {/* Вопрос своей правки — с кнопками ответа (или «ответ принят» и текстом,
                                                    который не влез); закрытый — закрытым, с причиной и этим текстом. */}
                                                {fx.question &&
                                                    (questionState(fx, fixes).phase === "closed" ||
                                                        (isQuestionOpen(fx, fixes) && isMine(fx, me))) && (
                                                    <FixQuestion
                                                        mediaId={mediaId}
                                                        fix={fx}
                                                        fixes={fixes}
                                                        onAnswered={() => void refresh()}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                        {fixes.length > visibleFixes(fixes, dismissed).length && (
                                            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                                                {hiddenLine(fixes, dismissed)}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    "—"
                                ),
                        },
                    ]}
                />
                {data?.fixes_error && <div style={{ color: "#cf1322", marginTop: 8 }}>{data.fixes_error}</div>}
            </Modal>
        </>
    );
};
